import { useState, useEffect, useCallback } from 'react';
import { vendorPaymentAPI } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { formatDateTime } from '../lib/utils';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Plus, Loader2, ReceiptText, AlertOctagon, AlertTriangle, Download } from 'lucide-react';
import { CreateRequestModal } from '../components/vendor-payment/CreateRequestModal';
import { RequestDetailModal } from '../components/vendor-payment/RequestDetailModal';

const STATUS_BADGE = {
  DRAFT: 'bg-slate-100 text-slate-700', CHECKED: 'bg-sky-100 text-sky-800',
  SUBMITTED: 'bg-amber-100 text-amber-800', PENDING_APPROVAL: 'bg-amber-100 text-amber-800',
  CHANGES_REQUESTED: 'bg-orange-100 text-orange-800',
  APPROVED: 'bg-emerald-100 text-emerald-800', APPROVED_FOR_PAYMENT: 'bg-emerald-100 text-emerald-800',
  PAID: 'bg-indigo-100 text-indigo-800',
  REJECTED: 'bg-red-100 text-red-800', CANCELLED: 'bg-slate-200 text-slate-600',
};

export default function VendorPaymentPage() {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const { user, canAccess } = useAuth();
  const isAccounts = user?.profile_id === 'accounts_profile';
  const [statusFilter, setStatusFilter] = useState(isAccounts ? 'APPROVED_FOR_PAYMENT' : 'all');
  const [scope, setScope] = useState('all');
  const [createOpen, setCreateOpen] = useState(false);
  const [detailId, setDetailId] = useState(null);
  const [exportOpen, setExportOpen] = useState(false);
  const [exportStatus, setExportStatus] = useState('all');
  const [exportFrom, setExportFrom] = useState('');
  const [exportTo, setExportTo] = useState('');
  const [exporting, setExporting] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter !== 'all') params.status = statusFilter;
      if (scope === 'pending_my_approval') params.pending_my_approval = true;
      if (scope === 'mine') params.mine = true;
      const res = await vendorPaymentAPI.list(params);
      setRequests(res.data || []);
    } catch (error) {
      toast.error('Failed to load vendor payment requests');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, scope]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleExport = async () => {
    setExporting(true);
    try {
      const params = {};
      if (exportStatus !== 'all') params.status = exportStatus;
      if (exportFrom) params.from = exportFrom;
      if (exportTo) params.to = exportTo;
      const res = await vendorPaymentAPI.exportList(params);
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `vendor_payment_list_${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      setExportOpen(false);
    } catch (error) {
      toast.error('Failed to export payment list');
    } finally {
      setExporting(false);
    }
  };

  const flagSummary = (req) => {
    const flags = req.flags || [];
    const hard = flags.filter(f => f.severity === 'HARD_BLOCK').length;
    const blocking = flags.filter(f => f.severity === 'BLOCKING' && !f.justification).length;
    if (hard > 0) return <span className="inline-flex items-center gap-1 text-red-600 text-xs"><AlertOctagon className="h-3 w-3" />{hard} hard block</span>;
    if (blocking > 0) return <span className="inline-flex items-center gap-1 text-amber-600 text-xs"><AlertTriangle className="h-3 w-3" />{blocking} to justify</span>;
    return <span className="text-xs text-muted-foreground">—</span>;
  };

  return (
    <div className="space-y-6" data-testid="vendor-payment-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Vendor Payment Approval</h1>
          <p className="text-muted-foreground text-sm">Upload vendor PIs, check against the matching PO, and route for approval.</p>
        </div>
        {canAccess('VENDOR_PAYMENT_APPROVAL', 'CREATE') && (
          <Button className="rounded-sm" onClick={() => setCreateOpen(true)} data-testid="new-vp-request-btn">
            <Plus className="h-4 w-4 mr-1.5" />New Payment Request
          </Button>
        )}
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        <div className="flex rounded-sm border border-border overflow-hidden" data-testid="vp-scope-tabs">
          {[
            { key: 'all', label: 'All Visible' },
            { key: 'mine', label: 'My Requests' },
            ...(canAccess('VENDOR_PAYMENT_APPROVAL', 'APPROVE') ? [{ key: 'pending_my_approval', label: 'Pending My Approval' }] : []),
          ].map((tab) => (
            <button
              key={tab.key}
              onClick={() => setScope(tab.key)}
              data-testid={`vp-scope-tab-${tab.key}`}
              className={`px-3 py-1.5 text-sm font-medium transition-colors ${scope === tab.key ? 'bg-primary text-primary-foreground' : 'bg-background hover:bg-muted'}`}
            >
              {tab.label}
            </button>
          ))}
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-48" data-testid="vp-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="DRAFT">Draft</SelectItem>
            <SelectItem value="CHECKED">Checked</SelectItem>
            <SelectItem value="SUBMITTED">Submitted</SelectItem>
            <SelectItem value="APPROVED_FOR_PAYMENT">Approved for Payment</SelectItem>
            <SelectItem value="PAID">Paid</SelectItem>
            <SelectItem value="REJECTED">Rejected</SelectItem>
            <SelectItem value="CANCELLED">Cancelled</SelectItem>
            <SelectItem value="CHANGES_REQUESTED">Changes Requested</SelectItem>
          </SelectContent>
        </Select>
        {canAccess('VENDOR_PAYMENT_APPROVAL', 'EXPORT') && (
          <div className="relative ml-auto">
            <Button variant="outline" className="rounded-sm" onClick={() => setExportOpen(v => !v)} data-testid="vp-export-list-btn">
              <Download className="h-4 w-4 mr-1.5" />Export payment list
            </Button>
            {exportOpen && (
              <div className="absolute right-0 top-full mt-1 w-96 bg-card border rounded-sm shadow-lg z-50 p-3 space-y-2" data-testid="vp-export-panel">
                <Select value={exportStatus} onValueChange={setExportStatus}>
                  <SelectTrigger className="w-full" data-testid="vp-export-status"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Approved for Payment + Paid</SelectItem>
                    <SelectItem value="APPROVED_FOR_PAYMENT">Approved for Payment only</SelectItem>
                    <SelectItem value="PAID">Paid only</SelectItem>
                  </SelectContent>
                </Select>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label className="text-[11px] text-muted-foreground uppercase">From</Label>
                    <Input type="date" value={exportFrom} onChange={(e) => setExportFrom(e.target.value)} className="h-8 text-sm" data-testid="vp-export-from" />
                  </div>
                  <div>
                    <Label className="text-[11px] text-muted-foreground uppercase">To</Label>
                    <Input type="date" value={exportTo} onChange={(e) => setExportTo(e.target.value)} className="h-8 text-sm" data-testid="vp-export-to" />
                  </div>
                </div>
                <Button className="rounded-sm w-full" size="sm" disabled={exporting} onClick={handleExport} data-testid="vp-export-confirm-btn">
                  {exporting ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Download className="h-4 w-4 mr-1.5" />}Download .xlsx
                </Button>
              </div>
            )}
          </div>
        )}
      </div>

      <Card className="rounded-sm">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="bg-muted/50">
                <TableHead className="font-medium text-xs uppercase">PO Number</TableHead>
                <TableHead className="font-medium text-xs uppercase">Vendor</TableHead>
                <TableHead className="font-medium text-xs uppercase">PI Number</TableHead>
                <TableHead className="font-medium text-xs uppercase text-right">Amount</TableHead>
                <TableHead className="font-medium text-xs uppercase">Status</TableHead>
                <TableHead className="font-medium text-xs uppercase">Flags</TableHead>
                <TableHead className="font-medium text-xs uppercase">Uploaded By</TableHead>
                <TableHead className="font-medium text-xs uppercase">Date</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={8} className="text-center py-12"><Loader2 className="h-6 w-6 mx-auto animate-spin text-muted-foreground" /></TableCell></TableRow>
              ) : requests.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-12">
                    <ReceiptText className="h-10 w-10 mx-auto text-muted-foreground mb-2" />
                    <p className="text-muted-foreground">No vendor payment requests yet</p>
                  </TableCell>
                </TableRow>
              ) : requests.map((req) => (
                <TableRow key={req.id} className="table-row-hover cursor-pointer" onClick={() => setDetailId(req.id)} data-testid={`vp-request-row-${req.id}`}>
                  <TableCell className="font-mono text-xs font-medium text-primary">{req.po_number}</TableCell>
                  <TableCell className="text-sm max-w-[200px] truncate">{req.po_vendor_name}</TableCell>
                  <TableCell className="text-sm">{req.pi_number || <span className="text-muted-foreground italic">unreadable</span>}</TableCell>
                  <TableCell className="text-right font-mono text-sm">{req.currency_code || req.po_currency_code} {req.amount_requested}</TableCell>
                  <TableCell><Badge className={`text-[10px] rounded-sm ${STATUS_BADGE[req.status] || ''}`}>{req.status}</Badge></TableCell>
                  <TableCell>{flagSummary(req)}</TableCell>
                  <TableCell className="text-sm text-muted-foreground">{req.created_by_name}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">{formatDateTime(req.created_at)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <CreateRequestModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={(id) => { setCreateOpen(false); setDetailId(id); fetchData(); }}
      />
      <RequestDetailModal
        requestId={detailId}
        open={!!detailId}
        onClose={() => { setDetailId(null); fetchData(); }}
        onChanged={fetchData}
      />
    </div>
  );
}
